import { Trans } from "@lingui/react/macro";
import React from "react";
import * as styles from "./OfferCard.module.scss";
import PackageIcon from "components/PackageIcon/PackageIcon";
import {
  BaseCard,
  BaseCardTitle,
  BaseCardBody,
  BaseCardProps,
  BlockContentValue,
  BaseCardFooter,
  Button,
  stringPresent
} from "@silversea-ssc/ui-components-react";
import { BlockContent } from "components/BlockContent";
import { formatDate } from "src/utils/date";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import ExpiresOnChip from "components/ExpiresOnChip/ExpiresOnChip";
import type { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";

interface OfferCardProps {
  id: string;
  tokenValues: TokenValues;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  trackName?: string;
  background?: BaseCardProps["background"];
  title?: BlockContentValue;
  description?: BlockContentValue;
  icon?: string;
  iconUrl?: string;
  disclaimer?: string | React.JSX.Element;
  expirationDate?: Date;
}

const OfferCard: React.FC<OfferCardProps> = ({
  id,
  tokenValues,
  onClick = undefined,
  background = "neutral05",
  title,
  description,
  icon,
  iconUrl,
  trackName,
  disclaimer,
  expirationDate
}) => {
  const language = useLanguage();
  const formattedDate = expirationDate
    ? formatDate(expirationDate, language.ogLang, {
        day: "numeric",
        month: "long",
        year: "numeric"
      })
    : undefined;

  return (
    <BaseCard
      key={id}
      background={background}
      textAlign="center"
      trackingName={trackName}
    >
      <div className={styles.cardContent}>
        <BaseCardTitle className={styles.titleContainer}>
          {stringPresent(iconUrl) ? (
            <img
              src={iconUrl}
              className={styles.iconUrl}
              alt="promo icon"
              loading="lazy"
            />
          ) : (
            <React.Fragment>
              {icon && <PackageIcon icon={icon} />}
            </React.Fragment>
          )}

          <div className={styles.title}>
            {title && <BlockContent value={title} tokens={tokenValues} />}
          </div>
          {formattedDate && (
            <ExpiresOnChip
              background="neutral05"
              className={styles.expiresOnChip}
            >
              <Trans>Expires on {formattedDate}</Trans>
            </ExpiresOnChip>
          )}
        </BaseCardTitle>
        <BaseCardBody className={styles.body}>
          {description && (
            <BlockContent value={description} tokens={tokenValues} />
          )}
          {disclaimer && <div className={styles.disclaimer}>{disclaimer}</div>}
        </BaseCardBody>
        <BaseCardFooter>
          <Button
            renderAs="button"
            dataContentPiece="offer_card"
            variant="text"
            onClick={onClick}
          >
            <Trans id="cta-read-more">Read More</Trans>
          </Button>
        </BaseCardFooter>
      </div>
    </BaseCard>
  );
};

export default OfferCard;
